import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Input,
  InputGroup,
  InputLeftElement,
  VStack,
  Box,
  Text,
  Flex,
  Avatar,
  Badge,
  HStack,
  Kbd,
} from '@chakra-ui/react';
import { Search, User, CalendarHeart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { userService, eventService } from '../services/api';

export const GlobalSearchModal = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();

  const { data: usersData } = useQuery({
    queryKey: ['globalUsers'],
    queryFn: () => userService.getUsers(),
    enabled: isOpen,
  });

  const { data: eventsData } = useQuery({
    queryKey: ['globalEvents'],
    queryFn: () => eventService.getEvents(),
    enabled: isOpen,
  });

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const rawUsers = Array.isArray(usersData)
    ? usersData
    : Array.isArray(usersData?.data)
    ? usersData.data
    : [];

  const rawEvents = Array.isArray(eventsData)
    ? eventsData
    : Array.isArray(eventsData?.data)
    ? eventsData.data
    : [];

  const filteredUsers = query.trim()
    ? rawUsers.filter((u) => u.name?.toLowerCase().includes(query.toLowerCase()) || u.email?.toLowerCase().includes(query.toLowerCase())).slice(0, 4)
    : [];

  const filteredEvents = query.trim()
    ? rawEvents.filter((e) => e.title?.toLowerCase().includes(query.toLowerCase()) || e.category?.toLowerCase().includes(query.toLowerCase())).slice(0, 3)
    : [];

  const handleSelect = (path) => {
    navigate(path);
    onClose();
    setQuery('');
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay backdropFilter="blur(5px)" />
      <ModalContent _dark={{ bg: '#161F30', borderColor: 'gray.700' }} borderRadius="24px" overflow="hidden">
        <ModalBody p={0}>
          <Box p={4} borderBottom="1px solid" borderColor="gray.100" _dark={{ borderColor: 'gray.800' }}>
            <InputGroup>
              <InputLeftElement pointerEvents="none" h="100%">
                <Search size={20} color="#A0AEC0" />
              </InputLeftElement>
              <Input
                variant="unstyled"
                placeholder="Type a command or search users, events..."
                fontSize="md"
                fontWeight="500"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoFocus
                h="45px"
                pl={10}
              />
            </InputGroup>
          </Box>

          <Box p={4} maxH="400px" overflowY="auto">
            {query.trim() === '' ? (
              <VStack spacing={2} align="stretch">
                <Text fontSize="xs" fontWeight="bold" color="gray.400" px={2}>QUICK SHORTCUTS</Text>
                {[
                  { label: 'View All Users', path: '/users', icon: User },
                  { label: 'Browse Speed Dating Events', path: '/events', icon: CalendarHeart },
                ].map((item, idx) => (
                  <Flex
                    key={idx}
                    p={3}
                    borderRadius="14px"
                    align="center"
                    justify="space-between"
                    _hover={{ bg: 'pink.50', cursor: 'pointer' }}
                    _dark={{ _hover: { bg: 'rgba(255,77,109,0.1)' } }}
                    onClick={() => handleSelect(item.path)}
                  >
                    <HStack spacing={3}>
                      <item.icon size={16} color="#FF4D6D" />
                      <Text fontSize="xs" fontWeight="600">{item.label}</Text>
                    </HStack>
                    <Kbd fontSize="10px">Jump</Kbd>
                  </Flex>
                ))}
              </VStack>
            ) : (
              <VStack spacing={4} align="stretch">
                {filteredUsers.length > 0 && (
                  <Box>
                    <Text fontSize="xs" fontWeight="bold" color="gray.400" mb={2} px={2}>USERS</Text>
                    {filteredUsers.map((user) => (
                      <Flex
                        key={user.id || user._id}
                        p={2.5}
                        borderRadius="14px"
                        align="center"
                        justify="space-between"
                        _hover={{ bg: 'purple.50', cursor: 'pointer' }}
                        _dark={{ _hover: { bg: 'rgba(108,99,255,0.1)' } }}
                        onClick={() => handleSelect('/users')}
                      >
                        <Flex align="center" gap={3}>
                          <Avatar size="sm" name={user.name} src={user.avatarUrl} />
                          <Box>
                            <Text fontSize="xs" fontWeight="bold">{user.name}</Text>
                            <Text fontSize="10px" color="gray.500">{user.email}</Text>
                          </Box>
                        </Flex>
                        <Badge size="sm" colorScheme="purple">{user.subscription || 'User'}</Badge>
                      </Flex>
                    ))}
                  </Box>
                )}

                {filteredEvents.length > 0 && (
                  <Box>
                    <Text fontSize="xs" fontWeight="bold" color="gray.400" mb={2} px={2}>EVENTS</Text>
                    {filteredEvents.map((evt) => (
                      <Flex
                        key={evt.id || evt._id}
                        p={2.5}
                        borderRadius="14px"
                        align="center"
                        justify="space-between"
                        _hover={{ bg: 'pink.50', cursor: 'pointer' }}
                        _dark={{ _hover: { bg: 'rgba(255,77,109,0.1)' } }}
                        onClick={() => handleSelect('/events')}
                      >
                        <Box>
                          <Text fontSize="xs" fontWeight="bold">{evt.title}</Text>
                          <Text fontSize="10px" color="gray.500">{evt.category} • {evt.location}</Text>
                        </Box>
                        <Badge size="sm" colorScheme="pink">{evt.status || 'Active'}</Badge>
                      </Flex>
                    ))}
                  </Box>
                )}
              </VStack>
            )}
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
